import {useState} from 'react';

import {useIFrameModalStore} from 'presentation/pages/private/services/store/iFrameModalStore';
import {useExternalServiceStore} from 'presentation/pages/private/store/externalSerivceUrlStore';
import {EmergencyTelehealthRoomUnavailableError} from 'presentation/components/EmergencyTelehealthUnavailableRoomModal/error';
import {AuthURlResponse, EndpointParamsProps, URLResponse} from './types';

const PDF_HEADER = '%PDF-';

function findPdfBase64(value: unknown): string | null {
	if (typeof value === 'string') {
		const data = value.replace(/^data:application\/pdf;base64,/, '').replace(/\s/g, '');
		return data.startsWith('JVBER') ? data : null;
	}

	if (value && typeof value === 'object') {
		for (const nestedValue of Object.values(value)) {
			const base64 = findPdfBase64(nestedValue);
			if (base64) return base64;
		}
	}

	return null;
}

async function normalizePdfBlob(response: Blob): Promise<Blob> {
	const content = await response.text();
	if (content.startsWith(PDF_HEADER)) {
		return new Blob([response], {type: 'application/pdf'});
	}

	let parsedContent: unknown = content;
	try {
		parsedContent = JSON.parse(content);
	} catch {
		// The BFF can also return Base64 as plain text.
	}

	const base64 = findPdfBase64(parsedContent);
	if (!base64) throw new TypeError('Invalid PDF response');

	const binary = atob(base64);
	const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
	const header = new TextDecoder().decode(bytes.slice(0, PDF_HEADER.length));
	if (header !== PDF_HEADER) throw new TypeError('Invalid PDF response');

	return new Blob([bytes], {type: 'application/pdf'});
}

export function useGetEndpointUrl(itemTitle: string) {
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const iFrameOpen = useIFrameModalStore(state => state.isIFrameOpen);
	const setIFrameOpen = useIFrameModalStore(state => state.setIFrameOpen);
	const setExternalServiceUrl = useExternalServiceStore(state => state.setUrl);

	const getLink = async <T>(
		endpoint: EndpointParamsProps<T>,
		openInIframe?: boolean,
		isBlob?: boolean,
		params?: T,
	) => {
		if (!isLoading) {
			setIsLoading(true);
			try {
				const response = await endpoint().get(params);
				if (isBlob) {
					if (!(response instanceof Blob)) {
						throw new TypeError('Expected a Blob response');
					}
					const blob = await normalizePdfBlob(response);
					const blobUrl = URL.createObjectURL(blob);
					window.open(blobUrl, '_blank');
					return true;
				}
				const url =
					typeof response === 'string'
						? response
						: (response as URLResponse).url || (response as AuthURlResponse).auth_url;
				setExternalServiceUrl(itemTitle, url);
				if (openInIframe) {
					setIFrameOpen(true);
					return true;
				}
				window.open(url, '_blank', 'noopener noreferrer');
				return true;
			} catch (err) {
				if (err instanceof EmergencyTelehealthRoomUnavailableError) {
					return null;
				}
				setIsError(true);
				return false;
			} finally {
				setIsLoading(false);
			}
		}

		return false;
	};
	return {isLoading, isError, setIsLoading, setIsError, getLink, iFrameOpen, setIFrameOpen};
}
